import { motion } from "framer-motion";
import { AlertCircle, Loader2 } from "lucide-react";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, ease: "easeOut", delay }}
  >
    {children}
  </motion.div>
);

export const Accent = ({ text = "" }) =>
  String(text).split(/(\*[^*]+\*)/g).map((part, i) =>
    part.startsWith("*") && part.endsWith("*") && part.length > 2
      ? <span key={i} className="eb-accent">{part.slice(1, -1)}</span>
      : <span key={i}>{part}</span>
  );

export const Section = ({ id, index, title, subtitle, children, className = "", action }) => (
  <section id={id} className={`py-20 md:py-28 ${className}`} data-testid={`section-${id}`}>
    <div className="eb-container">
      {(title || index) && (
        <Reveal className="mb-12 md:mb-16 grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            {index && <p className="eb-label mb-4 text-[#0055FF]">{index}</p>}
            {title && <h2 className="text-3xl md:text-5xl font-semibold tracking-[-0.035em]"><Accent text={title} /></h2>}
            {subtitle && <p className="mt-4 text-zinc-400 text-base md:text-lg max-w-xl">{subtitle}</p>}
          </div>
          {action && <div className="md:col-span-4 md:text-right">{action}</div>}
        </Reveal>
      )}
      {children}
    </div>
  </section>
);

export const Loading = ({ label = "Loading", rows = 3 }) => (
  <div className="grid gap-4 md:grid-cols-3" aria-busy="true" aria-label={label} data-testid="loading-state">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-64 bg-[#121212] border border-[#27272A] animate-pulse" />
    ))}
  </div>
);

export const Spinner = ({ className = "" }) => <Loader2 className={`animate-spin ${className}`} size={18} aria-hidden />;

export const Empty = ({ title, body, children }) => (
  <div className="border border-dashed border-[#27272A] p-10 md:p-14" data-testid="empty-state">
    <p className="font-display text-xl text-white">{title}</p>
    {body && <p className="mt-2 text-zinc-500 max-w-lg">{body}</p>}
    {children && <div className="mt-6">{children}</div>}
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-6 flex items-start gap-3" role="alert" data-testid="error-state">
    <AlertCircle className="text-[#FF3B30] shrink-0" size={20} />
    <div>
      <p className="text-white">{message}</p>
      {onRetry && <button onClick={onRetry} className="mt-2 text-sm text-[#00E5FF] underline" data-testid="retry-btn">Try again</button>}
    </div>
  </div>
);
